import { APP_INITIALIZER } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { ClientDataCacheService } from '../services/client-data-cache.service';
import { getSubdomainOrDomain } from '../utils/utils';
import { SafeWindow } from '../utils/safe-window';
import { environment } from '../../environments/environment';

/**
 * Non-blocking initializer - loads client data in background
 * App bootstrap is NOT delayed, ensuring faster first paint
 */
export function clientDataInitializerFactory(
  clientDataCache: ClientDataCacheService,
) {
  return () => {
    const win = SafeWindow.get();
    const unpublishOrgTournamentRegisterSubdomain =
      environment.unpublishOrgTournamentRegisterSubdomain;

    if (!win) {
      return Promise.resolve();
    }

    const hostname = window.location.hostname;
    const subdomain = getSubdomainOrDomain(hostname);

    // `ng serve` runs on a bare localhost host, which has no web-setting row,
    // so this lookup 400s and ClientService's catch sends the browser to
    // sketchplay.com — i.e. no page in this repo can be reviewed locally.
    // Skipping the lookup on loopback hosts only affects `ng serve`; every
    // real host still resolves its org exactly as before. Org subdomains on
    // loopback (e.g. gatekeeper.localhost) do have a row, so they still load —
    // the header and website gate both depend on this cached org.
    const isLocalHost =
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname === '[::1]';

    // Load client data in background WITHOUT blocking app bootstrap
    if (
      subdomain &&
      !isLocalHost &&
      !subdomain?.includes('qrc-registration') &&
      !subdomain?.includes(unpublishOrgTournamentRegisterSubdomain)
    ) {
      // Start loading but don't wait for completion
      lastValueFrom(clientDataCache.getClientData(subdomain))
        .then(() => {
          // Data loaded - header will display updated content
        })
        .catch(() => {
          // Client data failed to load during initialization - non-fatal
        });
    }

    // Always resolve immediately - app bootstrap happens right away
    return Promise.resolve();
  };
}

/**
 * Provider to inject the initializer into the app
 */
export const CLIENT_DATA_INITIALIZER = {
  provide: APP_INITIALIZER,
  useFactory: clientDataInitializerFactory,
  deps: [ClientDataCacheService],
  multi: true,
};
